'use client'

import { useEffect, useState } from 'react'
import { redirect, usePathname, useRouter } from 'next/navigation'
import { Router } from 'next/router'
import capitalize from 'just-capitalize'
import { handlePlural, isValidUUID, routes } from 'src/lib/utils'
import { AccountResponse } from 'types/accounts'
import { SiteResponse } from 'types/sites'

import { ButtonV2, LightProgressLoader, NotFoundCloud404 } from '@fsg/gui-bits'
import { useRequestCallback } from '@fsg/next-auth'

import { ENDPOINTS } from '@app/constants'

import { LightbulbLoading } from './LightbulbLoading'

type Props = {
  header?: string
  body?: string | JSX.Element
  redirectPath?: string
  btnText?: string
}

function useParsedPathWithData(pathname: string) {
  const pathnameArr = pathname.split('/')
  const { request } = useRequestCallback()
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const [entity, setEntity] = useState<{
    singular: string
    header: string
    body: string | JSX.Element
    redirectPath: string
    btnText: string
    data: AccountResponse | {}
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      const headerString = (input: string) => `${capitalize(input)} Not Found`
      const bodyString = (input: string) => `The ${input} you are looking for doesn't exist or has been deleted.`

      if (pathnameArr.includes('sites')) {
        const idIndex = pathnameArr.findIndex((x) => x === 'sites')
        const siteId = pathnameArr[idIndex + 1]

        try {
          //try to fetch the site ID and retrieve the account owner
          const site = await request<SiteResponse>({ path: `${ENDPOINTS.SITES}/${siteId}`, method: 'GET' })
          const account = site.owner
          setEntity({
            singular: 'site',
            header: headerString('site'),
            body: bodyString('site'),
            redirectPath: `/accounts/${account.id}/sites`,
            btnText: `See all Sites for ${account.name}`,
            data: account,
          })
        } catch (err) {
          //if failure to fetch, send user to /accounts
          setEntity({
            singular: 'site',
            header: headerString('site'),
            body: bodyString('site'),
            redirectPath: '/accounts',
            btnText: 'See all Accounts',
            data: {},
          })
        }
      } else {
        const entity = pathnameArr.find((x) => x === 'accounts' || x === 'contacts' || x === 'leads' || x === 'opportunities')

        if (entity === 'opportunities') {
          const idIndex = pathnameArr.findIndex((x) => x === 'opportunities')
          const entityId = pathnameArr[idIndex + 1]
          const redirectPath = routes.opportunity.base(entityId)
          if (pathname !== redirectPath && isValidUUID(entityId)) {
            setEntity({
              singular: 'opportunity',
              header: headerString('page'),
              body: (
                <>
                  {`${bodyString('page')}`}
                  <div className="p-sm"></div>
                  {'Did you mean '}
                  <span className="font-bold">{`${baseUrl}${redirectPath}`}</span>
                  {'?'}
                </>
              ),
              redirectPath: redirectPath,
              btnText: 'Go to Opportunity',
              data: {},
            })
          } else {
            setEntity({
              singular: 'opportunity',
              header: headerString('opportunity'),
              body: bodyString('opportunity'),
              redirectPath: '/opportunities',
              btnText: 'See all Opportunities',
              data: {},
            })
          }
        } else if (entity) {
          const idIndex = pathnameArr.findIndex((x) => x === entity)
          const entityId = pathnameArr[idIndex + 1]
          const entityRoute = Object.keys(routes).find((key) => key.toLowerCase() === entity.slice(0, -1).toLowerCase())

          // ? This type is a stop-gap, allows indexing the `routes` object without breaking intellisense by making `routes` an `any` object
          type RedirectRoutes = 'account' | 'lead' | 'contact' | 'opportunity' | 'site' | 'campaign'
          const redirectPath = routes[entityRoute as RedirectRoutes].base(entityId)
          if (pathname !== redirectPath && isValidUUID(entityId)) {
            setEntity({
              singular: entity.slice(0, -1),
              header: headerString('page'),
              //line break under the string, bold the url
              body: (
                <>
                  {`${bodyString('page')}`}
                  <div className="p-sm"></div>
                  {'Did you mean '}
                  <span className="font-bold">{`${baseUrl}${redirectPath}`}</span>
                  {'?'}
                </>
              ),
              redirectPath: redirectPath,
              btnText: `Go to ${capitalize(entity.slice(0, -1))}`,
              data: {},
            })
          } else {
            setEntity({
              singular: entity.slice(0, -1),
              header: headerString(entity.slice(0, -1)),
              body: bodyString(entity.slice(0, -1)),
              redirectPath: `/${entity}`,
              btnText: `See all ${capitalize(entity)}`,
              data: {},
            })
          }
        } else {
          setEntity({
            singular: 'page',
            header: headerString('page'),
            body: bodyString('page'),
            redirectPath: '/',
            btnText: 'Go Home',
            data: {},
          })
        }
      }

      setLoading(false)
    }

    if (!entity) {
      fetchData()
    }
  }, [pathname, pathnameArr, request, entity])

  return { entity, loading }
}
export function Page404({ header, body, redirectPath, btnText }: Props) {
  const pathname = usePathname()
  const { entity, loading } = useParsedPathWithData(pathname)
  const router = useRouter()

  function handleOnClick() {
    router.push(redirectPath || entity.redirectPath)
  }

  return (
    <div className="grid justify-center p-[25vh]">
      <div className=" grid w-[400px] items-center justify-items-center gap-sm rounded-lg bg-gray-200 p-[24px]">
        {loading && <LightProgressLoader />}
        {!loading && entity && (
          <>
            <NotFoundCloud404 />
            <h2 className="text-3xl font-bold">{header || entity.header}</h2>
            <p className="text-center text-sm">{body || entity.body}</p>
            <ButtonV2 onClick={() => router.push(redirectPath || entity.redirectPath)} type="button" className="mt-[24px] shadow-md">
              {btnText || entity.btnText}
            </ButtonV2>
          </>
        )}
      </div>
    </div>
  )
}
